import React from "react";
import {
  Diamond,
  Users,
  Settings2,
  MessageCircle,
  Cpu,
  Rocket,
  Target,
} from "lucide-react";

const points = [
  {
    icon: Diamond,
    title: "Boutique approach",
    desc: "Hand-built systems — not templated agency work.",
  },
  {
    icon: Users,
    title: "Small client roster",
    desc: "We onboard a limited number of clients to stay obsessed with results.",
  },
  {
    icon: Settings2,
    title: "Custom systems",
    desc: "Every workflow, funnel, and agent is engineered around your business.",
  },
  {
    icon: MessageCircle,
    title: "Direct communication",
    desc: "Talk to the builders. No account managers, no handoffs.",
  },
  {
    icon: Cpu,
    title: "Advanced technology",
    desc: "We deploy modern LLMs, voice agents, and integrations — production-grade.",
  },
  {
    icon: Rocket,
    title: "Fast implementation",
    desc: "Most systems go live in weeks — not quarters.",
  },
  {
    icon: Target,
    title: "Focused on real growth",
    desc: "Metrics that move the business: leads booked, hours saved, revenue captured.",
  },
];

const NetworkVisual = () => (
  <div className="relative aspect-square w-full max-w-md mx-auto">
    <div className="absolute inset-0 bg-[#39FF14]/10 blur-[100px] rounded-full" />
    <div className="absolute inset-8 bg-[#007AFF]/15 blur-[100px] rounded-full" />

    <svg
      viewBox="0 0 400 400"
      className="relative w-full h-full"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="cubeGrad" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#39FF14" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#007AFF" stopOpacity="0.6" />
        </radialGradient>
        <linearGradient id="lineGrad" x1="0" x2="1">
          <stop offset="0%" stopColor="#39FF14" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#007AFF" stopOpacity="0.2" />
        </linearGradient>
      </defs>

      {/* concentric rings */}
      <circle
        cx="200"
        cy="200"
        r="160"
        fill="none"
        stroke="rgba(255,255,255,0.06)"
      />
      <circle
        cx="200"
        cy="200"
        r="110"
        fill="none"
        stroke="rgba(255,255,255,0.08)"
        className="dash-anim"
      />
      <circle
        cx="200"
        cy="200"
        r="60"
        fill="none"
        stroke="rgba(57,255,20,0.25)"
      />

      {/* center cube */}
      <g transform="translate(200,200)">
        <rect
          x="-26"
          y="-26"
          width="52"
          height="52"
          rx="10"
          fill="url(#cubeGrad)"
        />
        <rect
          x="-26"
          y="-26"
          width="52"
          height="52"
          rx="10"
          fill="none"
          stroke="rgba(255,255,255,0.4)"
        />
      </g>

      {/* satellites */}
      {[
        [80, 110],
        [320, 130],
        [70, 290],
        [330, 290],
        [200, 60],
        [200, 340],
      ].map(([x, y], i) => (
        <g key={i}>
          <line
            x1="200"
            y1="200"
            x2={x}
            y2={y}
            stroke="url(#lineGrad)"
            strokeWidth="1"
            strokeDasharray="4 4"
            className="dash-anim"
          />
          <rect
            x={x - 8}
            y={y - 8}
            width="16"
            height="16"
            rx="3"
            fill="#0A0A0A"
            stroke="rgba(57,255,20,0.6)"
          />
          <rect
            x={x - 4}
            y={y - 4}
            width="8"
            height="8"
            rx="1.5"
            fill="#39FF14"
            opacity="0.85"
          />
        </g>
      ))}
    </svg>

    {/* Floating labels */}
    <div className="absolute top-4 left-2 glass rounded-xl px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-300 animate-float-medium">
      Voice AI
    </div>
    <div className="absolute top-8 right-4 glass rounded-xl px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-300 animate-float-slow">
      CRM Sync
    </div>
    <div className="absolute bottom-8 left-6 glass rounded-xl px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-300 animate-float-slow">
      Workflows
    </div>
    <div className="absolute bottom-6 right-2 glass rounded-xl px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-300 animate-float-medium">
      Analytics
    </div>
  </div>
);

export default function WhyCoreScale() {
  return (
    <section
      id="why"
      className="relative section-padding bg-[#070707] border-y border-white/5"
      data-testid="why-section"
    >
      <div className="absolute inset-0 dot-bg opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6 reveal">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#39FF14]">
            <span className="w-6 h-px bg-[#39FF14]" />
            Why Core Scale
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            Built Different. <br />
            Built For <span className="gradient-text-green">Growth.</span>
          </h2>
          <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-xl">
            We don't sell retainers. We deploy systems — owned by you, designed
            around your operations, engineered to compound.
          </p>

          <div className="mt-9 grid sm:grid-cols-2 gap-3">
            {points.map((p, i) => (
              <div
                key={p.title}
                className="flex gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.015] hover:border-[#39FF14]/25 hover:bg-white/[0.025] transition-all"
                data-testid={`why-point-${i + 1}`}
              >
                <div className="w-9 h-9 rounded-lg bg-[#39FF14]/10 border border-[#39FF14]/25 flex items-center justify-center text-[#39FF14] shrink-0">
                  <p.icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-white font-medium text-sm">
                    {p.title}
                  </div>
                  <div className="text-zinc-500 text-xs mt-0.5 leading-relaxed">
                    {p.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 reveal">
          <NetworkVisual />
        </div>
      </div>
    </section>
  );
}
